import { Popover, PopoverContent, PopoverTrigger } from "@gettactic/components";
import {
  AlignHorizontalSpaceAround,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp
} from "lucide-react";
import React from "react";
import { Shape, SpotOrientation, SpotShape } from "../../../../../types";
import { TooltipProvider } from "../../../../ui/tooltip";
import { useMapCanvas } from "../../../contexts/MapCanvasContext";
import { useMapEditorActions } from "../../../contexts/MapEditorContext";
import { generateNextSpotLabels } from "../../../helpers/generateNextSpotLabels";
import { getAbsolutePositionOfShape } from "../../../helpers/getAbsolutePositionOfShape";
import { getElementSize } from "../../../helpers/getElementSize";
import { getWindowKonvaObject } from "../../../helpers/konva/getWindowKonvaObject";
import { useShapeMetrics } from "../../../helpers/shapeMetrics";
import { DragNumberInput } from "../../controls/ScrollDragNumberInput";
import { ShapeControlButton } from "./controls/ShapeControlButton";

export interface ResourceToolBox {
  shapes: Shape[];
}

const DUPLICATE_MARGIN = 2;

export const ResourceToolBox: React.FC<ResourceToolBox> = ({ shapes }) => {
  const { duplicateShape, updateShapes, updateShape } = useMapEditorActions();
  const { lastSpotLabel, spotLabelPattern, setLastSpotLabel } = useMapCanvas();

  const [preserveSpacing, _setPreserveSpacing] = React.useState(false);
  const [horizontalSpacing, setHorizontalSpacing] = React.useState(0);
  const [verticalSpacing, setVerticalSpacing] = React.useState(0);

  const metrics = useShapeMetrics();
  const stage = metrics ? undefined : getWindowKonvaObject().stages[0];
  const measure = (shapeId: string) =>
    metrics ? metrics.getElementSize(shapeId) : getElementSize(shapeId, stage);

  const absPos = (shapes[0]
    ? metrics
      ? (() => {
          const rect = metrics.getScreenRect(shapes[0].id);
          if (!rect) return undefined;
          const centerX = (rect.left + rect.right) / 2;
          return { x: centerX, y: rect.top, xEnd: centerX, yEnd: rect.bottom };
        })()
      : getAbsolutePositionOfShape(shapes[0].id, stage)
    : undefined) || {
    x: 0,
    y: 0,
    xEnd: 0,
    yEnd: 0
  };

  const sizes = React.useMemo(() => {
    return shapes.map((shape) => measure(shape.id));
  }, [shapes]);

  const duplicateShapes = (direction: "right" | "bottom" | "left" | "top") => {
    if (!shapes.length) return;

    // Use visual bounds (getClientRect) for all shapes — gives actual rendered size
    // Transformer is rendered outside the shape Group, so it's not included
    const gMinX = Math.min(...sizes.map((s) => s?.visualX ?? 0));
    const gMinY = Math.min(...sizes.map((s) => s?.visualY ?? 0));
    const gMaxX = Math.max(
      ...sizes.map((s) => (s?.visualX ?? 0) + (s?.visualWidth ?? 0))
    );
    const gMaxY = Math.max(
      ...sizes.map((s) => (s?.visualY ?? 0) + (s?.visualHeight ?? 0))
    );
    const groupWidth = gMaxX - gMinX;
    let groupHeight = gMaxY - gMinY;

    // When orientation flips top↔bottom, the visual content shifts within the
    // shape group (e.g. top desk: visual y=0–90, bottom desk: visual y=10–100).
    // Correct the offset so the visual gap between old and new shapes is exact.
    // Skip for composites — companions don't move on flip, so visual padding
    // is not symmetric between orientations.
    const hasComposite = shapes.some(
      (s) => s.type === "spot" && (s as SpotShape).companions?.length
    );
    if (!hasComposite && (direction === "bottom" || direction === "top")) {
      const hasVerticalFlip = shapes.some((s) => {
        if (s.type !== "spot") return false;
        const ori = (s as SpotShape).orientation;
        return ori === "top" || ori === "bottom";
      });
      if (hasVerticalFlip) {
        const shapeMinY = Math.min(...shapes.map((s) => s.y));
        const shapeMaxY = Math.max(...shapes.map((s) => s.y + s.height));
        const topPad = gMinY - shapeMinY;
        const bottomPad = shapeMaxY - gMaxY;
        if (direction === "bottom") {
          groupHeight += topPad - bottomPad;
        } else {
          groupHeight += bottomPad - topPad;
        }
      }
    }

    const override: (Partial<Shape> & Required<Pick<Shape, "id">>)[] = [];
    const duplicateIds = shapes.map((shape) => shape.id);

    let lastSpotNumber: string[] | null = lastSpotLabel || [];

    shapes.forEach((shape) => {
      let x = shape.x;
      let y = shape.y;
      let orientation: SpotOrientation | undefined;

      if (direction === "right") {
        x = shape.x + groupWidth + DUPLICATE_MARGIN;
      } else if (direction === "left") {
        x = shape.x - groupWidth - DUPLICATE_MARGIN;
      } else if (direction === "bottom") {
        y = shape.y + groupHeight + DUPLICATE_MARGIN;
      } else if (direction === "top") {
        y = shape.y - groupHeight - DUPLICATE_MARGIN;
      }

      if (shape.type === "spot") {
        const spotShape = shape as SpotShape;
        orientation = spotShape.orientation;

        if (direction === "right" || direction === "left") {
          if (orientation === "left") orientation = "right";
          else if (orientation === "right") orientation = "left";
        } else if (direction === "top" || direction === "bottom") {
          if (orientation === "top") orientation = "bottom";
          else if (orientation === "bottom") orientation = "top";
        }
      }

      if (direction === "right" || direction === "bottom") {
        lastSpotNumber = generateNextSpotLabels(
          spotLabelPattern,
          lastSpotNumber || []
        );
      }

      duplicateIds.push(shape.id);
      override.push({
        id: shape.id,
        x,
        y,
        orientation,
        ...(direction === "right" || direction === "bottom"
          ? { label: lastSpotNumber?.join("") || "" }
          : {})
      });
    });

    duplicateShape(duplicateIds, {
      override,
      selectDuplicates: true
    });

    if (direction === "right" || direction === "bottom") {
      setLastSpotLabel(lastSpotNumber);
    }
  };

  const _duplicateShapesHorizontally = () => {
    duplicateShapes("right");
  };

  const _duplicateShapesVertically = () => {
    duplicateShapes("bottom");
  };

  const _duplicateShapesToLeft = () => {
    duplicateShapes("left");
  };

  const _duplicateShapesToTop = () => {
    duplicateShapes("top");
  };

  const _moveShapes = ({
    preserveSpacing,
    horizontalSpacing,
    verticalSpacing
  }: {
    preserveSpacing: boolean;
    horizontalSpacing: number;
    verticalSpacing: number;
  }) => {
    if (!shapes.length) return;

    function groupBy<T>(
      list: T[],
      keyGetter: (item: T) => string
    ): Map<string, T[]> {
      const map = new Map<string, T[]>();
      list.forEach((item) => {
        const key = keyGetter(item);
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key)!.push(item);
      });
      return map;
    }

    const TOLERANCE = 10; // Tolerance for grouping rows/columns

    // ----- Horizontal Spacing -----
    if (horizontalSpacing > 0) {
      const horizontalOrientations = ["left", "right", "top", "bottom"];

      for (const orientation of horizontalOrientations) {
        const shapesByOrientation = shapes.filter(
          (s) => (s as SpotShape).orientation === orientation
        );

        // Group shapes by approximate Y (row)
        const groups = groupBy(
          shapesByOrientation,
          (s) => `${Math.round((s.y || 0) / TOLERANCE) * TOLERANCE}`
        );

        for (const group of groups.values()) {
          const sorted = [...group].sort((a, b) => (a.x || 0) - (b.x || 0));
          // Track position using visual left edge
          const firstSize = measure(sorted[0]?.id);
          let currentVisualX = firstSize?.visualX ?? (sorted[0]?.x || 0);

          for (let i = 0; i < sorted.length; i++) {
            const shape = sorted[i];
            const size = measure(shape.id);
            if (!size) continue;

            // Offset from shape position to visual left edge
            const visualOffsetX = size.visualX - size.x;
            updateShape(shape.id, { x: currentVisualX - visualOffsetX });

            currentVisualX += size.visualWidth + horizontalSpacing;
          }
        }
      }
    }

    // ----- Vertical Spacing -----
    if (verticalSpacing > 0) {
      const verticalOrientations = ["left", "right", "top", "bottom"];

      for (const orientation of verticalOrientations) {
        const shapesByOrientation = shapes.filter(
          (s) => (s as SpotShape).orientation === orientation
        );

        // Group shapes by approximate Y (row)
        const rows = groupBy(
          shapesByOrientation,
          (s) => `${Math.round((s.y || 0) / TOLERANCE) * TOLERANCE}`
        );

        // Sort rows by their Y position
        const sortedRows = [...rows.entries()].sort(
          (a, b) => Number(a[0]) - Number(b[0])
        );

        if (sortedRows.length <= 1) continue;

        const firstRowSize = measure(sortedRows[0][1][0]?.id);
        let currentVisualY =
          (firstRowSize?.visualY ?? (sortedRows[0][1][0]?.y || 0)) +
          (firstRowSize?.visualHeight || 0) +
          verticalSpacing;

        for (let r = 1; r < sortedRows.length; r++) {
          const rowShapes = sortedRows[r][1];

          for (const shape of rowShapes) {
            const size = measure(shape.id);
            if (!size) continue;
            const visualOffsetY = size.visualY - size.y;
            updateShape(shape.id, { y: currentVisualY - visualOffsetY });
          }

          const rowSize = measure(rowShapes[0]?.id);
          currentVisualY += (rowSize?.visualHeight || 0) + verticalSpacing;
        }
      }
    }
  };

  if ((!metrics && !stage) || !shapes.length) return null;

  return (
    <TooltipProvider>
      <div
        style={{
          left: (absPos.x + absPos.xEnd) / 2,
          top: absPos.yEnd + 10,
          transform: "translateX(-50%)"
        }}
        className={
          "resource-tool-box -translate-x-1/2 fade-in-0 zoom-in-95 fixed z-sticky flex animate-in items-center gap-0.5 rounded-lg border border-neutral-200 bg-background-primary p-1 shadow-lg"
        }
      >
        <ShapeControlButton
          className="text-blue-600"
          onClick={() => _duplicateShapesHorizontally()}
          title={"Add Row (Right)"}
          icon={ArrowRight}
        />
        <ShapeControlButton
          className="text-blue-600"
          onClick={() => _duplicateShapesVertically()}
          title={"Add Column (Bottom)"}
          icon={ArrowDown}
        />
        <ShapeControlButton
          className="text-blue-600"
          onClick={() => _duplicateShapesToTop()}
          title={"Add Column (Top)"}
          icon={ArrowUp}
        />
        <ShapeControlButton
          className="text-blue-600"
          onClick={() => _duplicateShapesToLeft()}
          title={"Add Row (Left)"}
          icon={ArrowLeft}
        />
        {shapes.length > 1 && (
          <>
            <div className="h-4 w-px bg-neutral-200" />

            <Popover>
              <PopoverTrigger>
                <ShapeControlButton
                  className="text-green-600"
                  onClick={() => {}}
                  title={"Move with spacing"}
                  icon={AlignHorizontalSpaceAround}
                />
              </PopoverTrigger>
              <PopoverContent className="w-64">
                <div className="flex flex-col gap-4">
                  {/*<div>
                    <label className="mb-2 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={preserveSpacing}
                        onChange={(e) => {
                          setPreserveSpacing(e.target.checked);
                          _moveShapes({
                            preserveSpacing: e.target.checked,
                            horizontalSpacing,
                            verticalSpacing
                          });
                        }}
                      />
                      <span className="font-medium text-sm">
                        Preserve Spacing
                      </span>
                    </label>
                  </div>*/}

                  <div>
                    <div className="mb-2 font-medium text-sm">
                      Horizontal spacing
                    </div>
                    <DragNumberInput
                      size="sm"
                      value={horizontalSpacing || 0}
                      onChange={(value) => {
                        setHorizontalSpacing(value);
                        _moveShapes({
                          preserveSpacing,
                          horizontalSpacing: value,
                          verticalSpacing
                        });
                      }}
                      min={0}
                      max={200}
                    />
                  </div>

                  <div>
                    <div className="mb-2 font-medium text-sm">
                      Vertical spacing
                    </div>
                    <DragNumberInput
                      size="sm"
                      value={verticalSpacing || 0}
                      onChange={(value) => {
                        setVerticalSpacing(value);
                        _moveShapes({
                          preserveSpacing,
                          horizontalSpacing,
                          verticalSpacing: value
                        });
                      }}
                      min={0}
                      max={200}
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}
      </div>
    </TooltipProvider>
  );
};
